import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { Rocket, Users, Mail, Target } from 'lucide-react';
import { PreviewCard } from './PreviewCard';

export const LaunchPreviewStep = ({ listName, campaignData, onLaunch, isLaunching }) => {
  const profileCriteria = campaignData?.profileCriteria || 'Profile criteria will be displayed here';
  const emailPreview = campaignData?.emailPreview || 'Email preview will be displayed here';
  const estimatedReach = campaignData?.estimatedReach || '~500-1000';

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'neutral.50',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          maxWidth: '800px',
          margin: '0 auto',
          padding: '48px 24px',
          gap: '24px',
        }}
      >
        {/* Header */}
        <Flex sx={{ flexDirection: 'column', gap: '8px', textAlign: 'center' }}>
          <Text sx={{ fontSize: '24px', fontWeight: 600, color: 'text.dark' }}>Ready to launch your agent</Text>
          <Text sx={{ fontSize: '14px', color: 'text.main' }}>
            Review your campaign settings before launching the agent to grow &quot;{listName}&quot;
          </Text>
        </Flex>

        {/* Profile Criteria Card */}
        <PreviewCard icon={Target} title="Profile criteria">
          <Text sx={{ fontSize: '14px', color: 'text.main', lineHeight: 1.5 }}>{profileCriteria}</Text>
        </PreviewCard>

        {/* Email Preview Card */}
        <PreviewCard icon={Mail} title="Outreach email preview">
          <Box
            sx={{
              backgroundColor: 'neutral.50',
              borderRadius: '4px',
              p: '12px',
              fontSize: '14px',
              color: 'text.main',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {emailPreview}
          </Box>
        </PreviewCard>

        {/* Estimated Reach Card */}
        <PreviewCard icon={Users} title="Estimated reach">
          <Flex sx={{ alignItems: 'baseline', gap: '8px' }}>
            <Text sx={{ fontSize: '32px', fontWeight: 600, color: 'text.dark' }}>{estimatedReach}</Text>
            <Text sx={{ fontSize: '14px', color: 'text.subtle' }}>people will be contacted</Text>
          </Flex>
        </PreviewCard>

        {/* Launch Button */}
        <Flex sx={{ justifyContent: 'center', mt: '16px' }}>
          <Button
            variant="primary"
            size="large"
            onClick={onLaunch}
            loading={isLaunching}
            disabled={isLaunching}
            icon={Rocket}
            iconPosition="right"
            sx={{ minWidth: '200px' }}
          >
            Launch agent
          </Button>
        </Flex>

        {/* Disclaimer */}
        <Text sx={{ fontSize: '12px', color: 'text.subtle', textAlign: 'center' }}>
          By launching, you confirm that you have the necessary permissions to contact these individuals and that your
          outreach complies with applicable laws and regulations.
        </Text>
      </Flex>
    </Box>
  );
};

LaunchPreviewStep.propTypes = {
  listName: PropTypes.string.isRequired,
  campaignData: PropTypes.shape({
    profileCriteria: PropTypes.string,
    emailPreview: PropTypes.string,
    estimatedReach: PropTypes.string,
  }),
  onLaunch: PropTypes.func.isRequired,
  isLaunching: PropTypes.bool,
};

LaunchPreviewStep.defaultProps = {
  campaignData: null,
  isLaunching: false,
};
